import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { LucideAngularModule, Search, MessageSquare, Phone, Clock, User, ArrowLeft, Calendar, ChevronRight, Eye, MessageCircleOff, Copy } from 'lucide-angular';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DataTableComponent, TableColumn, TableAction, TableConfig } from '../../shared/components/data-table';
import { SessionDetailModalComponent } from './components/session-detail-modal.component';
import { 
  ConversationVisualizerService, 
  ConversationSession,
  SessionHeader
} from '../../core/services/conversation-visualizer.service';
import { ChatbotManagementService } from '../../core/services/chatbot-management.service';
import { Chatbot } from '../../core/models/chatbot.model';
import { Environment } from '../../core/models/environment.model';
import { RecentItemsService } from '../../core/services/recent-items.service';
import { EmptyStateComponent } from '../../shared/components/empty-state/empty-state.component';

@Component({
  selector: 'app-conversation-viewer',
  standalone: true,
  imports: [
    FormsModule,
    RouterModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatTooltipModule,
    MatDialogModule,
    LucideAngularModule,
    DataTableComponent,
    EmptyStateComponent
],
  templateUrl: './conversation-viewer-page.component.html',
  styleUrls: ['./conversation-viewer-page.component.scss']
})
export class ConversationViewerPageComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  readonly conversationService = inject(ConversationVisualizerService);
  readonly chatbotService = inject(ChatbotManagementService);
  readonly snackBar = inject(MatSnackBar);
  readonly route = inject(ActivatedRoute);
  readonly router = inject(Router);
  readonly dialog = inject(MatDialog);
  readonly recentItemsService = inject(RecentItemsService);

  // Capture chatbot from navigation state (passed from selector)
  private readonly navigationChatbot = this.router.getCurrentNavigation()?.extras?.state?.['chatbot'] as Chatbot | undefined;

  // Icons
  readonly SearchIcon = Search;
  readonly MessageSquareIcon = MessageSquare;
  readonly PhoneIcon = Phone;
  readonly ClockIcon = Clock;
  readonly UserIcon = User;
  readonly ArrowLeftIcon = ArrowLeft;
  readonly CalendarIcon = Calendar;
  readonly ChevronRightIcon = ChevronRight;
  readonly EyeIcon = Eye;
  readonly MessageCircleOffIcon = MessageCircleOff;
  readonly CopyIcon = Copy;
  // Table columns
  sessionColumns: TableColumn<SessionHeader>[] = [
    { key: 'sessionId', header: 'Session ID', type: 'text', searchable: true, formatter: (v: string) => v ? `${v.substring(0, 8)}...` : '-' },
    { key: 'phoneNumber', header: 'Teléfono', type: 'text', searchable: true, formatter: (v: string) => v || '-' },
    { key: 'startedAt', header: 'Fecha Inicio', type: 'text', sortable: true, formatter: (v: string) => this.formatTimestamp(v) },
    { key: 'sessionSource', header: 'Fuente', type: 'text', formatter: (v: string) => v || '-' },
    { key: 'messageCount', header: 'Msgs', type: 'number', sortable: true }
  ];

  // Table actions
  sessionActions: TableAction<SessionHeader>[] = [
    { icon: 'visibility', lucideIcon: this.EyeIcon, tooltip: 'Ver mensajes', color: 'primary', callback: (s) => this.selectSessionById(s.sessionId) },
    { icon: 'content_copy', lucideIcon: this.CopyIcon, tooltip: 'Copiar Session ID', callback: (s) => this.copyToClipboard(s.sessionId) }
  ];

  // Table config
  tableConfig: Partial<TableConfig> = { 
    searchable: false, 
    pageable: false, 
    stickyHeader: true, 
    emptyMessage: 'No hay sesiones', 
    emptyLucideIcon: this.MessageCircleOffIcon,
    maxHeight: '570px',
    infiniteScroll: true,
    scrollThreshold: 100
  };
  
  // Loading more sessions
  isLoadingMore = signal(false);

  // Source filter
  sourceFilter = signal<string>('all');
  sourceOptions = ['all', 'customFront', 'whatsapp'];
  
  // Phone filter
  phoneFilter = signal<string>('');
  
  // Combined filter function
  filterSessions = (session: SessionHeader) => {
    const sourceMatch = this.sourceFilter() === 'all' || session.sessionSource === this.sourceFilter();
    const phoneMatch = !this.phoneFilter() || (session.phoneNumber?.includes(this.phoneFilter()) ?? false);
    return sourceMatch && phoneMatch;
  };
  
  // State
  searchId = '';
  searchDate: Date | null = null;
  searchDateStr = '';
  environment = signal<Environment>('dev');
  botId = signal<string>('b2c-support-r2');
  currentChatbot = signal<Chatbot | null>(null);
  selectedParam = signal<string>('date');
  paramValue = signal<string>('');
  environments: Environment[] = ['dev', 'stg', 'prd'];
  isLoading = signal(false);
  isLoadingParams = signal(true);
  sessions = signal<SessionHeader[]>([]);
  loadedSessionMessages = signal<Map<string, ConversationSession>>(new Map());
  hasMoreSessions = signal(false);
  nextToken = signal<string | null>(null);

  // available params from chatbot
  availableParams = computed(() => {
    const chatbot = this.currentChatbot();
    if (!chatbot) return [];
    
    const params = (chatbot as any).conversationVisualizerParams || [];
    
    return [...params];
  });

  // currently selected param info
  currentParamInfo = computed(() => {
    const param = this.availableParams().find(p => p.queryParam === this.selectedParam());
    return param || null;
  });

  // search mode based on which field has value
  searchMode = computed<'date' | 'conversationId' | null>(() => {
    if (this.searchDate) return 'date';
    if (this.searchId.trim()) return 'conversationId';
    return null;
  });

  ngOnInit(): void {
    // Get botId from route params
    const routeBotId = this.route.snapshot.paramMap.get('botId');
    if (routeBotId) {
      this.botId.set(routeBotId);
    }

    // Use chatbot from navigation state if available, otherwise fallback to API call
    if (this.navigationChatbot) {
      // Direct navigation from selector - use passed chatbot data
      this.currentChatbot.set(this.navigationChatbot);
      
      // Save conversation visualizer to recent items (with unique ID to avoid duplication)
      this.recentItemsService.addRecentConversation(
        `conv_${this.navigationChatbot.code}`,
        this.navigationChatbot.name,
        this.navigationChatbot.code,
        ['/chatbot', 'conversations', this.navigationChatbot.code]
      );
      
      this.loadQueryParams();
    } else {
      // Direct URL access or refresh - fallback to loading from API
      this.loadChatbot();
    }
  }

  private loadQueryParams(): void {
    const firstParam = this.availableParams()[0];
    if (firstParam) {
      this.selectedParam.set(firstParam.queryParam);
    }
    this.isLoadingParams.set(false);
  }

  // If date is chosen -> clear conversation ID
  onDateChange(event: any): void {
    if (event.value) {
      this.searchId = ''; // Clear conversation ID when date is selected
    }
  }

  // If param value is typed -> clear session ID
  onParamValueInput(): void {
    if (this.paramValue().trim()) {
      this.searchId = '';
    }
  }

  // If session ID is typed -> clear params
  onSessionIdInput(): void {
    if (this.searchId.trim()) {
      this.searchDate = null;
      this.searchDateStr = '';
      this.paramValue.set('');
    }
  }

  // check if session ID is valid (sessionId_sessionId or conversationId_sessionId)
  private isValidSessionId(sessionId: string): boolean {
    const parts = sessionId.split('_');
    if (parts.length === 2 && parts[0].length > 0 && parts[1].length > 0) {
      return true;
    }
    else {     
      this.snackBar.open('El Session ID debe tener el formato conversationId_sessionId o sessionId_sessionId', 'Cerrar', { duration: 5000 }); 
      return false;
    }
  }

  // Perform search based on session ID or selected param
  search(): void {
    let conversationParams: Record<string, any>;

    // If searching by Session ID
    if (this.searchId.trim()) {
      if (!this.isValidSessionId(this.searchId.trim())) {
        return;
      }
      conversationParams = { sessionId: this.searchId.trim() };
      this.executeSearch(conversationParams);
      return;
    }

    // Otherwise, search by selected parameter
    const paramInfo = this.currentParamInfo();
    if (!paramInfo) {
      this.snackBar.open('Selecciona un parámetro de búsqueda', 'Cerrar', { duration: 3000 });
      return;
    }

    // Get value based on parameter type
    let value: any;

    if (paramInfo.dataType === 'datetime') {
      if (!this.searchDate) {
        this.snackBar.open(`Ingresa ${paramInfo.label}`, 'Cerrar', { duration: 3000 });
        return;
      }
      value = this.formatDateForApi(this.searchDate);
    } else {
      const paramVal = this.paramValue().trim();
      if (!paramVal) {
        this.snackBar.open(`Ingresa ${paramInfo.label}`, 'Cerrar', { duration: 3000 });
        return;
      }
      
      if (paramInfo.dataType === 'number') {
        value = Number(paramVal);
      } else if (paramInfo.dataType === 'boolean') {
        value = paramVal === 'true';
      } else {
        value = paramVal;
      }
    }

    conversationParams = { [paramInfo.queryParam]: value };
    this.executeSearch(conversationParams);
  }

  // Execute search with given conversationParams
  private executeSearch(conversationParams: Record<string, any>): void {
    this.isLoading.set(true);
    this.clearResults();

    this.conversationService.runChatbotConversationQuery(
      this.botId(),
      conversationParams,
      this.environment()
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.sessions.set(response.sessions);
        this.hasMoreSessions.set(response.hasMore);
        this.nextToken.set(response.nextToken || null);
        this.isLoading.set(false);
        
        const sessionCount = response.count;
        
        if (sessionCount === 0) {
          this.snackBar.open('No se encontraron sesiones.', 'Cerrar', { duration: 3000 });
        } else if (sessionCount === 1) {
          this.selectSession(0);
          this.snackBar.open('1 sesión encontrada', 'Cerrar', { duration: 2000 });
        } else {
          this.snackBar.open(
            `${sessionCount} sesiones encontradas${response.hasMore ? ' (hay más disponibles)' : ''}.`,
            'Cerrar',
            { duration: 3000 }
          );
        }
      },
      error: (error) => {
        this.isLoading.set(false);
        const errorMsg = error.error?.responseData || error.message || 'Error al cargar las sesiones.';
        this.snackBar.open(errorMsg, 'Cerrar', { duration: 5000 });
      }
    });
  }

  selectSession(index: number): void {
    const sessionsList = this.sessions();
    if (!sessionsList?.[index]) return;
    
    const session = sessionsList[index];
    const sessionId = session.sessionId;
    const conversationId = session.conversationId;

    let sessionIdToFilter = sessionId + "_" + sessionId
    if (conversationId) {
      sessionIdToFilter = conversationId + "_" + sessionId
    }
    
    // Check if messages are already loaded
    const cachedSession = this.loadedSessionMessages().get(sessionId);
    if (cachedSession) {
      this.openSessionModal(cachedSession);
      return;
    }
    
    // Load session with messages
    const loadingSnackbar = this.snackBar.open('Cargando mensajes...', undefined, { duration: undefined });

    this.conversationService.getSession(
      sessionIdToFilter,
      this.botId()
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        
        const loadedMap = new Map(this.loadedSessionMessages());
        loadedMap.set(sessionId, response.session);
        this.loadedSessionMessages.set(loadedMap);
        loadingSnackbar.dismiss();
        
        // Open modal with loaded data
        this.openSessionModal(response.session);
      },
      error: (error) => {
        loadingSnackbar.dismiss();
        this.snackBar.open(
          'Error al cargar los mensajes de la sesión.',
          'Cerrar',
          { duration: 5000 }
        );
      }
    });
  }

  private openSessionModal(session: ConversationSession): void {
    this.dialog.open(SessionDetailModalComponent, {
      data: {
        session: session,
        formatTimestamp: (timestamp: string) => this.formatTimestamp(timestamp),
        copyToClipboard: (text: string) => this.copyToClipboard(text)
      },
      width: '1300px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      panelClass: 'session-detail-dialog',
      autoFocus: false
    });
  }

  selectSessionById(sessionId: string): void {
    const sessionsList = this.sessions();
    const index = sessionsList.findIndex(s => s.sessionId === sessionId);
    if (index >= 0) {
      this.selectSession(index);
    }
  }

  onSessionRowClick(session: SessionHeader): void {
    this.selectSessionById(session.sessionId);
  }

  private clearResults(): void {
    this.sessions.set([]);
    this.loadedSessionMessages.set(new Map());
    this.hasMoreSessions.set(false);
    this.nextToken.set(null);
  }

  clearSearch(): void {
    this.searchId = '';
    this.searchDate = null;
    this.clearResults();
  }

  private formatDateForApi(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  goBack(): void {
    this.router.navigate(['/chatbot/conversations']);
  }

  formatTimestamp(timestamp: string): string {
    const date = new Date(Number.parseFloat(timestamp) * 1000);
    return date.toLocaleString('es-CL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.snackBar.open('ID copiado al portapapeles', 'Cerrar', {
        duration: 2000
      });
    });
  }

  loadChatbot(): void {
    this.chatbotService.getAllChatbots().pipe(takeUntil(this.destroy$)).subscribe({
      next: (chatbots) => {
        const chatbot = chatbots.find(c => c.code === this.botId());
        if (chatbot) {
          this.currentChatbot.set(chatbot);
          
          // Save conversation visualizer to recent items (with unique ID to avoid duplication)
          this.recentItemsService.addRecentConversation(
            `conv_${chatbot.code}`,
            chatbot.name,
            chatbot.code,
            ['/chatbot', 'conversations', chatbot.code]
          );
          
          this.loadQueryParams();
        } else {
          this.snackBar.open('Chatbot no encontrado.', 'Cerrar', { duration: 3000 });
          this.isLoadingParams.set(false);
        }
      },
      error: (error) => {
        console.error('Error al cargar chatbot:', error);
      }
    });
  }

  loadMoreSessions(): void {
    if (!this.nextToken() || this.isLoadingMore()) return;

    this.isLoadingMore.set(true);

    // Rebuild conversationParams from current search
    const paramInfo = this.currentParamInfo();
    if (!paramInfo) {
      this.isLoadingMore.set(false);
      return;
    }

    // Get value based on parameter type
    let value: any;
    if (paramInfo.dataType === 'datetime') {
      if (!this.searchDate) {
        this.isLoadingMore.set(false);
        return;
      }
      value = this.formatDateForApi(this.searchDate);
    } else {
      const paramVal = this.paramValue().trim();
      if (!paramVal) {
        this.isLoadingMore.set(false);
        return;
      }
      
      if (paramInfo.dataType === 'number') {
        value = Number(paramVal);
      } else if (paramInfo.dataType === 'boolean') {
        value = paramVal === 'true';
      } else {
        value = paramVal;
      }
    }

    const conversationParams = { [paramInfo.queryParam]: value };

    this.conversationService.runChatbotConversationQuery(
      this.botId(),
      conversationParams,
      this.environment(),
      this.nextToken() ?? ''
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        const currentSessions = this.sessions();
        this.sessions.set([...currentSessions, ...response.sessions]);
        this.hasMoreSessions.set(response.hasMore);
        this.nextToken.set(response.nextToken || null);
        this.isLoadingMore.set(false);
      },
      error: (error) => {
        this.isLoadingMore.set(false);
        this.snackBar.open('Error al cargar más sesiones.', 'Cerrar', { duration: 3000 });
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showMobileFilters = false;
  toggleMobileFilters() {
    this.showMobileFilters = !this.showMobileFilters;
  }
}

